import { Injectable } from '@angular/core';
import moment from 'moment';
import { BusRouteDataMap, DemoRegion } from '../mock-map/constant/route-types';
import { createTimeTables } from '../mock-map/demo-utils/demo-timetable';
import { createIssueTimetable } from '../mock-map/demo-service/process-timetable-issue';
import { extractBusArrMap } from './demo-timing-helper';
import {
  compareDates,
  isWithinTime,
  isXafterYtime,
  timeStringAddSeconds,
  timeStringRemoveSeconds,
  timeStringToMinutes,
} from '../mock-map/demo-utils/demo-timer';
import { RouteTimetableData, Trip } from '../types/mock-timetable-type';

@Injectable({
  providedIn: 'root',
})
export class DemoTimingServiceService {
  constructor() {}

  timetable: RouteTimetableData[];
  prevTimetable;
  blockIds;
  vehicleMap;

  initTimetables(regionData: DemoRegion) {
    const { initialTimetable, availableBlockIds, isFixedTimetable, routeData } =
      regionData;
    this.timetable = createTimeTables(
      initialTimetable,
      availableBlockIds,
      isFixedTimetable
    );
    this.prevTimetable = JSON.parse(JSON.stringify(this.timetable));
    this.prevTimetable = createIssueTimetable(this.prevTimetable, routeData);
    this.vehicleMap = extractBusArrMap(routeData);
    // console.log({ vehicle: this.vehicleMap });
    this.blockIds = availableBlockIds;

    this.initTimetableSimulation(routeData);
  }

  isInitRoute = {};
  availableTrips: Trip[] = [];
  initTimetableSimulation(routeDataMap: BusRouteDataMap) {
    Object.entries(routeDataMap).forEach(([routeId, routeData]) => {
      const { busArr } = routeData;
      if (!this.isInitRoute[routeId]) {
        this.isInitRoute[routeId] = true;
        this.assignVehiclesToTimetable(routeId, busArr);
        const availableTrips =
          this.getAvailableTripsAndRecordPastTrips(routeId);
        this.availableTrips = [...this.availableTrips, ...availableTrips];
        const currentTrips = this.getBlockTripsByCurrenttime(routeId);
        // console.log('Current Trips: ', routeId, currentTrips);
      }
    });
    // console.log('Available Trips: ', this.availableTrips);
  }

  clearRoutes() {
    this.isInitRoute = Object.entries(this.isInitRoute).reduce(
      (prev, [key, value]) => {
        prev[key] = false;
        return prev;
      },
      {}
    );
    this.availableTrips = [];
  }

  /**
   * Creates past trips assuming vehicle is already assigned
   * @param routeId
   */
  getAvailableTripsAndRecordPastTrips(routeId: string) {
    const newTimeCurrent = moment().format('HH:mm:ss');
    const currTimetable = this.timetable.find(item => item.routeId === routeId);
    // console.log('get current timetable', routeId, currTimetable);
    const { timetable: timeTable, numBus } = currTimetable;
    const flattenedTimeTableTrips: Trip[] = timeTable.reduce((prev, curr) => {
      const { trips } = curr;
      trips.forEach(trip => (trip.routeId = routeId));
      return [...prev, ...trips];
    }, []);

    const sortedFlatTimeTableTrips = flattenedTimeTableTrips.sort((a, b) => {
      const { stops: stopsA } = a;
      const { stops: stopsB } = b;
      const firstStopA = stopsA[0];
      const firstStopB = stopsB[0];
      return compareDates(firstStopA?.schArrTime, firstStopB?.schArrTime);
    });

    // past trips
    const filteredFlatTimetableTrips = sortedFlatTimeTableTrips.filter(a => {
      const { stops: stopsA } = a;
      const firstStopA = stopsA[0];
      const lastStopA = stopsA[stopsA.length - 1];

      return (
        compareDates(firstStopA?.schArrTime, newTimeCurrent) < 0 &&
        compareDates(lastStopA?.schArrTime, newTimeCurrent) < 0
      );
    });

    // future trips
    const futureFlatTimetableTrips = sortedFlatTimeTableTrips.filter(a => {
      const { stops: stopsA, direction } = a;
      const firstStopA = stopsA[0];
      const lastStopA = stopsA[stopsA.length - 1];

      //  todo: allow multiple direction
      return (
        // direction === '1' &&
        (compareDates(firstStopA?.schArrTime, newTimeCurrent) > 0 &&
          compareDates(lastStopA?.schArrTime, newTimeCurrent) < 0) ||
        compareDates(lastStopA?.schArrTime, newTimeCurrent) > 0 ||
        compareDates(firstStopA?.schArrTime, newTimeCurrent) > 0
      );
    });

    filteredFlatTimetableTrips.forEach(trip => {
      const { stops, vehicleId } = trip;
      trip.isStarted = true;
      stops.forEach((stop, stopIndex) => {
        const { schArrTime } = stop;
        stop.schArrTime = timeStringRemoveSeconds(schArrTime);
        const randomSeconds = Math.floor(Math.random() * 61);
        stop.obsArrTime = timeStringAddSeconds(schArrTime, randomSeconds);
        stop.busRegNo = vehicleId;
        stop.actualHeadway = '-';
        stop.planHeadway = '-';
        stop.headwayStatusColor = 'bgGray';
        stop.isAdded = true;
        stop.statusColor = 'on-time';
      });
    });

    // console.log('future trips: ', routeId, {
    //   sortedFlatTimeTableTrips,
    //   futureFlatTimetableTrips,
    // });
    return futureFlatTimetableTrips;
  }

  assignVehiclesToTimetable(routeId, busArr) {
    const currTimetable = this.timetable.find(item => item.routeId === routeId);
    const { timetable: timeTable, numBus } = currTimetable;
    const flattenedTimeTableTrips: Trip[] = timeTable.reduce((prev, curr) => {
      const { trips } = curr;
      return [...prev, ...trips];
    }, []);

    const sortedFlatTimeTableTrips = flattenedTimeTableTrips.sort((a, b) => {
      const { stops: stopsA } = a;
      const { stops: stopsB } = b;
      const firstStopA = stopsA[0];
      const firstStopB = stopsB[0];
      return compareDates(firstStopA?.schArrTime, firstStopB?.schArrTime);
    });

    const blockIdSet = new Set<string>();

    sortedFlatTimeTableTrips.forEach(ttItem => {
      const { blockId } = ttItem;
      blockIdSet.add(blockId);
    });

    const blockIdList = [...blockIdSet];
    const vehicleBlockMap = blockIdList.reduce((acc, blockId, blockIndex) => {
      acc[blockId] = busArr[blockIndex % busArr.length];
      return acc;
    }, {});
    sortedFlatTimeTableTrips.forEach(ttItem => {
      const { blockId } = ttItem;
      ttItem.vehicleId = vehicleBlockMap[blockId];
    });
  }

  getTimeTableByBlock(routeId, blockId, tripId) {
    const currTimeTable = this.timetable.find(data => data.routeId === routeId);

    let tripIndex = -1;
    let tripDirection;
    const { stopList, timetable } = currTimeTable;
    const filteredTimeTable = timetable.map(dirTable => {
      const { direction, trips } = dirTable;
      let filteredTrips = trips.filter((trip, index) => {
        const { tripId: currTripId } = trip;
        if (tripId === currTripId) {
          // tripIndex = index;
          tripDirection = direction;
        }
        return trip.blockId === blockId;
      });

      return { direction, trips: filteredTrips };
    });

    const flattenedTable = filteredTimeTable.reduce((prev, acc) => {
      const { trips } = acc;
      return [...prev, ...trips];
    }, []);

    const sortedFlatTable = flattenedTable.sort((a, b) => {
      const minA = timeStringToMinutes(a.stops[0].schArrTime);
      const minB = timeStringToMinutes(b.stops[0].schArrTime);
      return minA - minB;
    });

    tripIndex = sortedFlatTable.findIndex((trip, index) => {
      if (trip.tripId === tripId) {
        tripDirection = trip.direction;
        return true;
      }
      return false;
    });

    return {
      currentTripData: { index: tripIndex, direction: tripDirection },
      timeTableData: {
        routeId,
        stopList,
        timetable: filteredTimeTable,
        flatTimeTable: sortedFlatTable,
      },
    };
  }

  getBlockTripsByCurrenttime(routeId) {
    const currTimetable = this.timetable.find(item => item.routeId === routeId);
    const newTimeCurrent = moment().format('HH:mm:ss');
    // const currBlockIds = this.blockIds[routeId];

    const { timetable: timeTable, numBus } = currTimetable;

    const flattenedTimeTableTrips: Trip[] = timeTable.reduce((prev, curr) => {
      const { trips } = curr;
      return [...prev, ...trips];
    }, []);

    const sortedFlatTimeTableTrips = flattenedTimeTableTrips.sort((a, b) => {
      const { stops: stopsA } = a;
      const { stops: stopsB } = b;
      const firstStopA = stopsA[0];
      const firstStopB = stopsB[0];
      return compareDates(firstStopA?.schArrTime, firstStopB?.schArrTime);
    });

    const blockIdSet = new Set<string>();

    sortedFlatTimeTableTrips.forEach(ttItem => {
      const { blockId } = ttItem;
      blockIdSet.add(blockId);
    });

    const blockIdList = [...blockIdSet];

    const blockTrips = blockIdList.map(blockId => {
      const filteredTrips = sortedFlatTimeTableTrips.filter(
        trip => trip.blockId === blockId
      );

      const tripObj = filteredTrips.find((trip, tripIndex) => {
        const { stops } = trip;
        const firstStop = stops[0];
        const lastStop = stops[stops.length - 1];

        // check if current time is within the trip
        const withinTime = isWithinTime(
          firstStop.schArrTime,
          lastStop.schArrTime,
          newTimeCurrent
        );

        // check if current time is before current trip but after previous trip
        let isBeforeTrip = false;
        if (tripIndex > 0) {
          const prevTrip = filteredTrips[tripIndex - 1];
          const { stops: prevStops } = prevTrip;
          const lastStopPrevTrip = prevStops[prevStops.length - 1];
          const isCurrTimeAfterPrevTrip = isXafterYtime(
            newTimeCurrent,
            lastStopPrevTrip.schArrTime
          );

          const isCurrTripAfterCurrTime = isXafterYtime(
            firstStop.schArrTime,
            newTimeCurrent
          );
          isBeforeTrip = isCurrTimeAfterPrevTrip && isCurrTripAfterCurrTime;
        }

        return withinTime || isBeforeTrip;
      });

      return tripObj;
    });
    return blockTrips;
  }

  timetableArrival({
    routeId,
    direction,
    tripId,
    stopId,
    obsArrTime,
    busRegNo,
  }) {
    try {
      const currTimetable = this.timetable.find(
        timetable => timetable.routeId === `${routeId}`
      );
      const { timetable } = currTimetable;
      const timetableDir = timetable.find(
        ttable => ttable.direction === `${direction}`
      );
      const { trips } = timetableDir;
      // console.log({ timetableDir });
      const currTrip = trips.find(item => item.tripId === tripId);

      // console.log('timetable arrival', routeId, direction, tripId, currTrip);

      const { stops } = currTrip;
      const currStop = stops.find(stop => stop.stopId === stopId);
      if (currStop) {
        // const formatPlanTime = this.commonService.formatPlanTime(currStop);

        // currStop.schArrTime = formatPlanTime;
        currStop.obsArrTime = obsArrTime;

        const formatActualTime = this.formatActualTime(currStop);
        const { obsArrTime: formatObsArrTime, statusColor } = formatActualTime;

        currStop.statusColor = statusColor;

        const retHeadway = this.formatHeadwayTime(currStop);
        currStop.headwayStatusColor = retHeadway.headwayStatusColor;
        currStop.planHeadway = '15min';
        currStop.actualHeadway =
          currStop.observedHeadway === 'null' ||
          currStop.observedHeadway === null ||
          currStop.observedHeadway === '0' ||
          currStop.observedHeadway === ''
            ? '-'
            : Math.round(+currStop.observedHeadway / 60) + 'min';
        currStop.deviationHeadway =
          currStop.headwayDeviation === 'null' ||
          currStop.headwayDeviation === null ||
          currStop.headwayDeviation === '0' ||
          currStop.headwayDeviation === ''
            ? '-'
            : Math.round(+currStop.headwayDeviation / 60) + 'min';

        currStop.busRegNo = busRegNo;
      }
    } catch (e) {
      console.log('timetable arrival error', e);
    }
  }

  /**
   *
   * @param stop
   * @param isTimezoneFix temporary fix to adjust time for SA-SG backend issue
   * @returns
   */
  formatActualTime(stop, isTimezoneFix = false) {
    var actualTime = '';
    var obsArrTime = '';
    if (stop.obsArrTime === '') {
      return {
        obsArrTime: '',
        statusColor: '',
      };
    }
    if (stop.obsArrTime) {
      if (stop.obsArrTime.split(' ')[1]) {
        actualTime = stop.obsArrTime.split(' ')[1];
      } else {
        actualTime = stop.obsArrTime;
      }
    }

    var plan = moment(stop.schArrTime, 'H:mm');
    var actual = moment(actualTime, 'H:mm').subtract(
      isTimezoneFix ? 5 : 0,
      'hour'
    );
    var ss = moment(actual).diff(moment(plan), 'seconds');

    if (!plan.isValid() && !actual.isValid() && stop.obsArrTime !== null) {
      let actualTimeInvalid = stop.obsArrTime.split(':');
      let planTimeInvalid = stop.schArrTime.split(':');
      let actTime = moment(
        actualTimeInvalid[0] - 24 + ':' + actualTimeInvalid[1],
        'H:mm'
      );
      let plaTime = moment(
        planTimeInvalid[0] - 24 + ':' + planTimeInvalid[1],
        'H:mm'
      );

      ss = moment(actTime).diff(moment(plaTime), 'seconds');

      // console.log(stop.stopId, plaTime, ss, actTime);
      obsArrTime = actualTimeInvalid[0] + ':' + actualTimeInvalid[1];
    } else if (stop.obsArrTime !== null) {
      if (stop.obsArrTime.split(' ')[1]) {
        if (isTimezoneFix) {
          let actualTimeFormat = stop.obsArrTime.split(' ')[1];
          const newTime = moment(actualTimeFormat, 'H:mm')
            .subtract(isTimezoneFix ? 5 : 0, 'hour')
            // .utcOffset('+08:00')
            // .utcOffset(environment.timezone)
            .format('H:mm');
          obsArrTime = newTime;
        } else {
          let actualTimeFormat = stop.obsArrTime.split(' ')[1].split(':');
          // obsArrTime = stop.obsArrTime === null ? '' : moment(stop.obsArrTime).format("HH:mm");
          obsArrTime = actualTimeFormat[0] + ':' + actualTimeFormat[1];
        }
      } else {
        if (isTimezoneFix) {
          const newTime = moment(stop.obsArrTime, 'H:mm')
            .subtract(isTimezoneFix ? 5 : 0, 'hour')
            // .utcOffset('+08:00')
            // .utcOffset(environment.timezone)
            .format('H:mm');
          obsArrTime = newTime;
        } else {
          obsArrTime = stop.obsArrTime;
        }
      }
    }

    let statusColor;
    if (ss <= -180) {
      //-240
      statusColor = 'early';
    } else if (ss >= -179 && ss <= 179) {
      //else if(ss >= -239 && ss <= 239) {
      statusColor = 'on-time';
    } else if (ss >= 180) {
      //240
      statusColor = 'late';
    }

    return {
      obsArrTime: obsArrTime,
      statusColor: statusColor,
    };
  }

  formatHeadwayTime(stop) {
    // var headwayPlanTime = 6;
    // var headwayActualTime = 5;
    // var diffValue = headwayPlanTime - headwayActualTime;

    if (stop.headwayDeviation === null) {
      return {
        headwayDeviation: headwayDeviation,
        headwayStatusColor: 'bgGray',
      };
    }

    var headwayDeviation = parseInt(stop.headwayDeviation) / 60;
    let statusColor;
    // if(headwayDeviation <= -3) {
    //   statusColor = 'early';
    // }
    // else if(headwayDeviation >= -3 && headwayDeviation < 3) {
    //   statusColor = 'on-time';
    // }
    // else if(headwayDeviation >= 3) {
    //   statusColor = 'late';
    // }

    if (stop.headwayDeviation <= -180) {
      //240
      statusColor = 'late';
    } else if (stop.headwayDeviation >= -179 && stop.headwayDeviation <= 179) {
      //else if(stop.headwayDeviation >= -239 && stop.headwayDeviation <= 239) {
      statusColor = 'on-time';
    } else if (stop.headwayDeviation >= 180) {
      //240
      statusColor = 'early';
    }

    return {
      headwayDeviation: headwayDeviation,
      headwayStatusColor: statusColor,
    };
  }
}
